import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import SchemaTagTableSchemaSelector from 'metadata/SchemaTagTableSchemaSelector';
import TagTable from 'metadata/TagTable';
import {calcTagTableErrorsByFieldID, useTagTableState} from 'metadata/useTagTableState';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import * as IntegrationTagUtil from 'schemas/tags/integration/IntegrationTagUtil';
import {getTagsToSet, getTagTableFriendlyTags, TagUpdateObject} from 'schemas/tags/TagUtil';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Modal from 'ui-library/modals/Modal';
import {BasicEvent} from 'util/Types';

import {TemplateCollectionGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';
import clampEntryName from './clampEntryName';
import TemplateCollectionSelector from './collections/TemplateCollectionSelector';

export interface CreateTemplateModalProps {
  onSubmit: (
    name: string,
    templateCollection: TemplateCollectionGraphQL,
    isProtected: boolean,
    tagSchema?: TagSchema | null,
    schemaTags?: TagUpdateObject
  ) => void;
  onRequestClose: () => void;
  initialTemplateCollection: TemplateCollectionGraphQL;
}

function CreateTemplateModal(props: CreateTemplateModalProps): JSX.Element {
  return (
    <CreateBaseTemplateModal
      {...props}
      title="Create template"
      enableFillInTemplate={BenchlingConfig.ELN_ENABLE_FILL_IN_TEMPLATES}
      enableSchemaOnEntry={BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL}
    />
  );
}

export function CreateSubtemplateModal(props: CreateTemplateModalProps): JSX.Element {
  return (
    <CreateBaseTemplateModal
      {...props}
      title="Create sub-template"
      enableFillInTemplate={false}
      enableSchemaOnEntry={false}
    />
  );
}

interface CreateBaseTemplateModalProps extends CreateTemplateModalProps {
  title: string;
  enableFillInTemplate: boolean;
  enableSchemaOnEntry: boolean;
}

function CreateBaseTemplateModal(props: CreateBaseTemplateModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [templateCollection, setTemplateCollection] = useState(props.initialTemplateCollection);
  const [isProtected, setIsProtected] = useState(false);
  const {tagSchema, schemaTags, updateTagSchema, updateSchemaTag} = useTagTableState();
  const errorsByFieldId = tagSchema ? calcTagTableErrorsByFieldID(tagSchema, schemaTags, false) : {};

  function handleChangeTemplateCollection(collection: TemplateCollectionGraphQL): void {
    setTemplateCollection(collection);
  }

  function manageCollections(e: BasicEvent): void {
    const url = TemplateCollectionDAO.collectionsURL();
    BenchlingUtil.navigate(e, url);
    props.onRequestClose();
  }

  function isSubmitEnabled(): boolean {
    const hasName = name.trim().length > 0;
    const hasTemplateCollection = templateCollection != null;
    const hasNoSchemaErrors = Object.keys(errorsByFieldId).length === 0;
    return hasName && hasTemplateCollection && hasNoSchemaErrors;
  }

  function submit(): void {
    assert(
      Object.keys(errorsByFieldId).length === 0,
      'Submit button should be disabled if there are errors present'
    );
    props.onSubmit(name, templateCollection, isProtected, tagSchema, getTagsToSet(schemaTags));
  }

  return (
    <Modal
      title={props.title}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={submit}
      buttons={[
        {text: 'Create', submit: true, disabled: !isSubmitEnabled()},
        {text: 'Manage Collections', onClick: manageCollections, asLink: true, pullLeft: true},
      ]}
      dataTestAttrs={{component: 'CreateTemplateModal', element: 'modal'}}
    >
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'CreateTemplateModal', element: 'name'}}>
            <Label>Name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(clampEntryName(e.target.value));
              }}
              autoFocus={true}
            />
          </FormGroup>
        </Col>
        <Col sm={5}>
          <Label>Template collection</Label>
          <TemplateCollectionSelector value={templateCollection} onChange={handleChangeTemplateCollection} />
        </Col>
      </Row>
      {props.enableSchemaOnEntry && (
        <Row>
          <Col sm={12}>
            <Label>Entry schema</Label>
            <TagTable
              canRefreshTags={
                ComputedTagUtil.hasComputedTags(getTagTableFriendlyTags(schemaTags)) ||
                IntegrationTagUtil.hasIntegrationTags(getTagTableFriendlyTags(schemaTags))
              }
              emptyMessage="This schema has no fields."
              errorsByFieldId={errorsByFieldId}
              tags={schemaTags}
              areTagsEditable={false}
              isLoadingTags={false}
              onAnalyzeProteins={noop}
              registryId={tagSchema?.registryId}
              sequenceParts={null}
              subheaderNode={
                <SchemaTagTableSchemaSelector
                  fileType={ModelTypeConstants.ENTRY}
                  isSchemaEditable={true}
                  isFileRegistered={false}
                  onUpdateTagSchema={updateTagSchema}
                  tagSchema={tagSchema}
                />
              }
              title={<div>Schema</div>}
              onUpdateTag={updateSchemaTag}
              isEditing={true}
            />
          </Col>
        </Row>
      )}
      {props.enableFillInTemplate && (
        <Row>
          <Col sm={12}>
            <Label>Fill-in only</Label>
            <CheckboxInput
              id="isProtectedCheckbox"
              checked={isProtected}
              label="Entries created from this template will be locked. Users can only fill in tables, text boxes,
          checkboxes, and files."
              onChange={(e) => {
                setIsProtected(e.target.checked);
              }}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
}

export default CreateTemplateModal;
